import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Play } from "lucide-react";
import Pipeline, { PostCard } from "./Pipeline";
import { Footer, Nav, WaitlistForm } from "./Layout";
import { CHANNELS, FAQ, FILM, LIMIT_ROWS, PLANS, WAVE_STATUS } from "./content";
import { CruiseLine, STAGES } from "../components/CruiseLine";

/* The landing page, per the revamp (PRD sections 19-22): mission control for
   content. Left-aligned, solid surfaces, status as the colour, the gradient
   only in the cruise line, and every visual a real artefact. */

const wrap = "mx-auto max-w-7xl px-4 sm:px-8";
const btn = "inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-[15px] font-medium transition-colors duration-fast ease-ac";
const primary = `${btn} bg-ac-action text-white hover:bg-ac-action-hover`;
const secondary = `${btn} border border-ac-border text-ac-text hover:bg-ac-raised`;

const Eyebrow = ({ children }) => <p className="mono-label text-ac-link">{children}</p>;
const H2 = ({ children }) => (
  <h2 className="mt-3 font-display text-[30px] sm:text-[40px] font-semibold leading-[1.1] text-ac-text">{children}</h2>
);
const Lede = ({ children }) => <p className="mt-4 max-w-2xl text-[16px] sm:text-[17px] leading-relaxed text-ac-muted">{children}</p>;

const PrimaryCta = ({ signupsOpen, signedIn, className = "" }) =>
  signedIn ? <Link to="/app" className={`${primary} ${className}`}>Open app</Link>
    : signupsOpen ? <Link to="/signup" className={`${primary} ${className}`}>Start free</Link>
      : <a href="#start" className={`${primary} ${className}`}>Join the waitlist</a>;

/* ----------------------------------- Hero ----------------------------------- */
const Hero = ({ signupsOpen, signedIn }) => (
  <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
    {/* The one subtle radial tint the revamp allows, hero only. */}
    <div className="pointer-events-none absolute inset-0" aria-hidden="true"
      style={{ background: "radial-gradient(60% 60% at 85% 20%, rgba(99,102,241,0.10), transparent 70%)" }} />
    <div className={`relative ${wrap} grid gap-12 lg:grid-cols-12 lg:items-center`}>
      <div className="lg:col-span-7">
        <Eyebrow>AI content autopilot · by Optimml</Eyebrow>
        <h1 className="mt-5 font-display text-[44px] sm:text-[64px] font-semibold leading-[1.02] tracking-tight text-ac-text">
          Your content team,<br className="hidden sm:block" /> on autopilot.
        </h1>
        <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-ac-muted">
          Finds the story, writes and designs the post, checks it and publishes to your site and
          channels. You approve what matters.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <PrimaryCta signupsOpen={signupsOpen} signedIn={signedIn} />
          <a href="#film" className={secondary}><Play className="w-4 h-4" /> Watch the {FILM.runtime} film</a>
        </div>
        <p className="mt-5 mono-label text-ac-muted">20 posts / month free · No card · You approve every post</p>
      </div>
      <div className="lg:col-span-5">
        <Pipeline />
      </div>
    </div>
  </section>
);

/* ----------------------------------- Channels ----------------------------------- */
const Channels = () => (
  <section className="border-y border-ac-border bg-ac-surface/40">
    <div className={`${wrap} py-8 grid gap-6 md:grid-cols-2`}>
      {[["Publishes to", CHANNELS.publishes], ["Approve from", CHANNELS.approves]].map(([label, items]) => (
        <div key={label}>
          <p className="mono-label text-ac-muted">{label}</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {items.map((c) => (
              <li key={c} className="rounded-md border border-ac-border bg-ac-surface px-3 py-1.5 text-[14px] text-ac-text">{c}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

/* ----------------------------------- Film ----------------------------------- */
const Film = () => {
  const [playing, setPlaying] = useState(false);
  const video = useRef(null);
  const seek = (t) => {
    const [m, s] = t.split(":").map(Number);
    setPlaying(true);
    requestAnimationFrame(() => {
      if (!video.current) return;
      video.current.currentTime = m * 60 + s;
      video.current.play();
    });
  };
  return (
    <section id="film" className="py-20 sm:py-28 scroll-mt-20">
      <div className={`${wrap} grid gap-10 lg:grid-cols-12`}>
        <div className="lg:col-span-4">
          <Eyebrow>The film · {FILM.runtime}</Eyebrow>
          <H2>One story, from feed to published.</H2>
          <Lede>No sound needed: every line is on screen. Jump to a chapter.</Lede>
          <ol className="mt-6 divide-y divide-ac-border border-y border-ac-border">
            {FILM.chapters.map(([t, name]) => (
              <li key={t}>
                <button onClick={() => seek(t)}
                  className="w-full flex items-center gap-4 py-2.5 text-left text-[15px] text-ac-text hover:text-ac-link">
                  <span className="font-mono text-[13px] text-ac-muted w-10">{t}</span>{name}
                </button>
              </li>
            ))}
          </ol>
        </div>
        <div className="lg:col-span-8">
          <div className="relative rounded-[10px] overflow-hidden border border-ac-border bg-black">
            <video ref={video} className="w-full aspect-video" src={FILM.src} poster={FILM.poster}
              playsInline muted controls={playing} preload="none" />
            {!playing && (
              <button onClick={() => seek("0:00")} aria-label="Play the film"
                className="absolute left-4 bottom-4 inline-flex items-center gap-2 h-11 px-4 rounded-md bg-ac-action text-white text-[15px] font-medium hover:bg-ac-action-hover">
                <Play className="w-4 h-4 fill-white" /> Play · {FILM.runtime}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ----------------------------------- How it works ----------------------------------- */
const HOW = [
  ["Listen", "Reads your feeds, pillars and last week's posts."],
  ["Plan", "Picks topics to hit the weekly target, balancing formats and pillars, never repeating."],
  ["Create", "Writes in your brand voice; designs the card, image or carousel."],
  ["Check", "Hard checks on every post; an editorial score against the brand if you want it."],
  ["Approve", "Manual, auto or quality mode. Held posts come to the app, Telegram or WhatsApp."],
  ["Publish", "Your site first, then social with a tagged link on the ratio you set."],
  ["Learn", "Edits, rejections and results tune voice, topics and timing."],
];

/* One post card advances along the line as the section scrolls past; without
   scrolling (or with reduced motion) it simply shows every stage done. */
const HowItWorks = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(STAGES.length - 1);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const on = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const progress = Math.min(1, Math.max(0, (window.innerHeight * 0.75 - r.top) / (r.height * 0.8)));
      setActive(Math.round(progress * (STAGES.length - 1)));
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <section id="how" ref={ref} className="py-20 sm:py-28 border-t border-ac-border scroll-mt-20">
      <div className={wrap}>
        <Eyebrow>How it works</Eyebrow>
        <H2>Seven stages. You choose where you step in.</H2>
        <div className="mt-12 hidden md:block">
          <CruiseLine active={active} />
        </div>
        <div className="mt-8 grid gap-6 lg:grid-cols-12">
          <ol className="lg:col-span-8 grid gap-px bg-ac-border border border-ac-border rounded-[10px] overflow-hidden sm:grid-cols-2">
            {HOW.map(([name, body], i) => (
              <li key={name} className={`bg-ac-surface p-5 ${i === HOW.length - 1 ? "sm:col-span-2" : ""}`}>
                <p className="flex items-center gap-3">
                  <span className="font-mono text-[13px] text-ac-muted">0{i + 1}</span>
                  <span className={`text-[17px] font-medium ${i <= active ? "text-ac-text" : "text-ac-muted"}`}>{name}</span>
                </p>
                <p className="mt-2 text-[15px] leading-relaxed text-ac-muted">{body}</p>
              </li>
            ))}
          </ol>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 rounded-[10px] border border-ac-border bg-ac-surface p-4">
              <p className="mono-label text-ac-muted">Now · {STAGES[active]}</p>
              <div className="mt-3 max-w-[260px]"><PostCard /></div>
              <p className="mt-3 text-[13px] text-ac-muted">
                {active < 3 ? "Drafting from the story in Signal Weekly." : active === 3 ? "Checking: 4 of 4 hard checks, 9.2 / 10 on-brand."
                  : active === 4 ? "Passed the gate in quality mode." : active === 5 ? "Scheduled to LinkedIn, Tue 9:00." : "Clicks feed next week's topics."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ----------------------------------- Product grid ----------------------------------- */
const MixBars = () => (
  <div className="space-y-3">
    {[["Viral news cards", 40], ["Single insight", 30], ["Carousels", 30]].map(([label, pct]) => (
      <div key={label}>
        <div className="flex justify-between text-[13px] text-ac-muted mb-1.5"><span>{label}</span><span className="font-mono">{pct}%</span></div>
        <div className="h-1.5 rounded-full bg-ac-raised overflow-hidden">
          <div className="h-full rounded-full bg-[#A5B4FC]" style={{ width: `${pct * 2}%` }} />
        </div>
      </div>
    ))}
    <p className="pt-1 font-mono text-[12px] text-ac-muted">Target 14 posts / week · 3 pillars</p>
  </div>
);

const GatePanel = () => (
  <div className="space-y-3">
    <p className="font-mono text-[28px] text-ac-text leading-none">6.0<span className="text-sm text-ac-muted"> / 10 · bar 8.0</span></p>
    <div className="rounded-md border border-[#6B1D47] bg-[#3B1029] p-3 text-[13px]">
      <p className="text-[#F9A8D4] font-medium">Held: asserts a figure with no source</p>
      <p className="mt-1 text-[#F9A8D4]/70">"62% of startups…" not found in Signal Weekly</p>
    </div>
    <ul className="grid grid-cols-2 gap-1.5 text-[13px]">
      {[["Caption complete", 1], ["No AI tells", 1], ["Image rendered", 1], ["No banned words", 1]].map(([c, ok]) => (
        <li key={c} className="flex items-center gap-2 text-ac-text">
          <span className={`w-2 h-2 rounded-full ${ok ? "bg-[#4ADE80]" : "bg-[#F472B6]"}`} />{c}
        </li>
      ))}
    </ul>
  </div>
);

const BrandKit = () => (
  <div className="space-y-4">
    <div className="flex gap-2">
      {["#0B0B12", "#F60C77", "#DB23B3", "#A946F8", "#FFFFFF"].map((c) => (
        <span key={c} className="flex-1 h-10 rounded-md border border-ac-border" style={{ background: c }} title={c} />
      ))}
    </div>
    <dl className="grid grid-cols-2 gap-y-1.5 text-[13px]">
      <dt className="text-ac-muted">Headline</dt><dd className="text-ac-text">Clash Grotesk 600</dd>
      <dt className="text-ac-muted">Voice</dt><dd className="text-ac-text">Plain, confident, no hype</dd>
      <dt className="text-ac-muted">Banned</dt><dd className="text-ac-text">"revolutionary", "game-changer"</dd>
    </dl>
  </div>
);

const TrafficLoop = () => (
  <div className="space-y-2 text-[13px]">
    {[["Article", "Published to yourbrand.com with featured image + SEO"],
      ["LinkedIn", "Link post · utm_source=linkedin&utm_medium=social"],
      ["Instagram", "Native post · link in bio line"],
      ["Facebook", "Link post · 1 in 2 carry the link"]].map(([k, v]) => (
      <div key={k} className="flex gap-3 rounded-md border border-ac-border bg-ac-raised px-3 py-2">
        <span className="w-20 shrink-0 text-ac-text">{k}</span><span className="text-ac-muted truncate">{v}</span>
      </div>
    ))}
  </div>
);

const ApprovalMessage = () => (
  <div className="max-w-[300px] rounded-[10px] border border-ac-border bg-ac-raised p-3 text-[13px]">
    <p className="mono-label text-ac-muted !text-[10px]">Telegram · AutoCruise</p>
    <p className="mt-2 text-ac-text">Held for review · Acme · score 6/10</p>
    <p className="mt-1 text-ac-muted">Asserts a figure with no source.</p>
    <div className="mt-3 grid grid-cols-3 gap-1.5 text-center text-[12px] font-medium">
      <span className="rounded-md bg-ac-action text-white py-1.5">Approve</span>
      <span className="rounded-md border border-ac-border text-ac-text py-1.5">Edit</span>
      <span className="rounded-md border border-[#6B2121] text-[#FCA5A5] py-1.5">Reject</span>
    </div>
  </div>
);

const Workspaces = () => (
  <ul className="divide-y divide-ac-border rounded-md border border-ac-border text-[13px]">
    {[["Acme Coffee", "Owner", "3 held"], ["Northwind Legal", "Admin", "1 held"], ["Signal Studio", "Editor", "All clear"]].map(([n, r, s]) => (
      <li key={n} className="flex items-center justify-between gap-3 px-3 py-2">
        <span className="text-ac-text">{n}</span>
        <span className="text-ac-muted">{r}</span>
        <span className={`font-mono text-[12px] ${s === "All clear" ? "text-[#86EFAC]" : "text-[#F9A8D4]"}`}>{s}</span>
      </li>
    ))}
  </ul>
);

const PRODUCT = [
  ["An engine that picks its own topics", "Keeps the calendar full to your weekly target, balancing formats and pillars, grounding news posts in the real article.", <MixBars key="m" />],
  ["A gate before anything goes live", "Hard checks always. An editorial score if you want it. Held posts carry the reason.", <GatePanel key="g" />],
  ["Your brand, every frame", "Palette, type, image rules, voice and banned words. Upload your guidelines PDF and check what it filled in.", <BrandKit key="b" />],
  ["Turn posts into website traffic", "The article goes to WordPress first, then social posts link to it on the ratio you choose, every link tagged.", <TrafficLoop key="t" />],
  ["Approve from anywhere", "Telegram or WhatsApp, with the preview, the score and the reason in the message.", <ApprovalMessage key="a" />],
  ["Teams and client workspaces", "Invite editors, keep each client isolated, switch in a click.", <Workspaces key="w" />],
];

const Product = () => (
  <section id="features" className="py-20 sm:py-28 border-t border-ac-border scroll-mt-20">
    <div className={wrap}>
      <Eyebrow>The product</Eyebrow>
      <H2>Everything a content team does, shown as it does it.</H2>
      <div className="mt-12 grid gap-4 md:grid-cols-2">
        {PRODUCT.map(([title, body, visual]) => (
          <article key={title} className="rounded-[10px] border border-ac-border bg-ac-surface p-6 flex flex-col">
            <h3 className="font-display text-[20px] sm:text-[22px] font-semibold text-ac-text">{title}</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-ac-muted">{body}</p>
            <div className="mt-6 pt-6 border-t border-ac-border">{visual}</div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ----------------------------------- Pricing ----------------------------------- */
const PlanCta = ({ plan, signupsOpen }) => {
  const cls = `${plan.featured ? primary : secondary} w-full`;
  if (plan.to.startsWith("mailto:")) return <a href={plan.to} className={cls}>{plan.cta}</a>;
  if (!signupsOpen) return <a href="#start" className={cls}>Join the waitlist</a>;
  return <Link to={plan.to} className={cls}>{plan.cta}</Link>;
};

const Pricing = ({ signupsOpen }) => (
  <section id="pricing" className="py-20 sm:py-28 border-t border-ac-border scroll-mt-20">
    <div className={wrap}>
      <Eyebrow>Pricing</Eyebrow>
      <H2>Start free. Grow into autopilot.</H2>
      <Lede>Every plan includes the brand system, the composer and approvals. The autonomous engine and auto-approval arrive with Pro.</Lede>
      <div className="mt-12 grid gap-4 lg:grid-cols-3">
        {PLANS.map((p) => (
          <div key={p.key} className={`rounded-[10px] bg-ac-surface p-6 flex flex-col border ${
            p.featured ? "border-[#6366F1]" : "border-ac-border"}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-[22px] font-semibold text-ac-text">{p.name}</h3>
              {p.featured && <span className="mono-label !text-[11px] text-ac-link">Most teams</span>}
            </div>
            <p className="mt-1 text-[15px] text-ac-muted">{p.blurb}</p>
            <p className="mt-5 flex items-baseline gap-2">
              <span className="font-display text-[44px] font-semibold text-ac-text leading-none">{p.price}</span>
              <span className="text-[14px] text-ac-muted">{p.period}</span>
            </p>
            <dl className="mt-6 flex-1 divide-y divide-ac-border border-y border-ac-border text-[14px]">
              {LIMIT_ROWS.slice(0, 5).map(([k, label]) => (
                <div key={k} className="flex justify-between py-2">
                  <dt className="text-ac-muted">{label}</dt><dd className="font-mono text-ac-text">{p.limits[k]}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6"><PlanCta plan={p} signupsOpen={signupsOpen} /></div>
          </div>
        ))}
      </div>

      <div className="mt-10 overflow-x-auto rounded-[10px] border border-ac-border">
        <table className="w-full min-w-[560px] text-[14px]">
          <caption className="sr-only">Plan comparison</caption>
          <thead className="bg-ac-surface">
            <tr>
              <th scope="col" className="text-left font-medium text-ac-muted px-4 py-3">Compare plans</th>
              {PLANS.map((p) => <th key={p.key} scope="col" className="text-left font-medium text-ac-text px-4 py-3">{p.name}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-ac-border">
            {LIMIT_ROWS.map(([k, label]) => (
              <tr key={k}>
                <th scope="row" className="text-left font-normal text-ac-muted px-4 py-2.5">{label}</th>
                {PLANS.map((p) => <td key={p.key} className="px-4 py-2.5 font-mono text-ac-text">{p.limits[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

/* ----------------------------------- FAQ ----------------------------------- */
const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-20 sm:py-28 border-t border-ac-border scroll-mt-20">
      <div className={`${wrap} grid gap-10 lg:grid-cols-12`}>
        <div className="lg:col-span-4">
          <Eyebrow>FAQ</Eyebrow>
          <H2>Questions, answered.</H2>
        </div>
        <div className="lg:col-span-8 divide-y divide-ac-border border-y border-ac-border">
          {FAQ.map(({ q, a }, i) => (
            <div key={q}>
              <button onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}
                className="w-full flex items-center justify-between gap-6 py-5 text-left">
                <span className="text-[17px] font-medium text-ac-text">{q}</span>
                <ChevronDown className={`w-5 h-5 text-ac-muted shrink-0 transition-transform duration-base ease-ac ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="pb-5 pr-10 text-[16px] leading-relaxed text-ac-muted">{a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ----------------------------------- Final CTA ----------------------------------- */
const FinalCta = ({ signupsOpen, signedIn }) => (
  <section id="start" className="py-20 sm:py-28 scroll-mt-20">
    <div className={wrap}>
      <div className="rounded-[10px] border border-ac-border bg-[#0B0C14] p-6 sm:p-12">
        <div className="cruise-line w-24" />
        <h2 className="mt-6 font-display text-[30px] sm:text-[44px] font-semibold leading-[1.08] text-ac-text">
          Hand over the calendar.<br />Keep the final say.
        </h2>
        <p className="mt-4 max-w-xl text-[17px] text-ac-muted">
          {signupsOpen ? "Set up your brand in minutes. Your first 20 posts each month are free."
            : "We're opening in waves. Leave your email and we'll tell you the moment yours opens."}
        </p>
        <div className="mt-8">
          {signupsOpen || signedIn ? <PrimaryCta signupsOpen={signupsOpen} signedIn={signedIn} /> : <WaitlistForm />}
        </div>
        {WAVE_STATUS && <p className="mt-6 mono-label text-ac-muted">{WAVE_STATUS}</p>}
      </div>
    </div>
  </section>
);

export default function Landing({ platform, signedIn, previewing }) {
  const signupsOpen = Boolean(platform?.signups_open) && !platform?.maintenance;
  return (
    <div className="min-h-screen bg-ac-bg text-ac-text overflow-x-hidden">
      {previewing && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] rounded-md border border-[#6B1D47] bg-[#3B1029] px-4 py-2 text-[13px] text-[#F9A8D4]">
          Maintenance is on. Only signed-in admins see this page; visitors see the maintenance page.
        </div>
      )}
      <Nav signupsOpen={signupsOpen} signedIn={signedIn} />
      <main>
        <Hero signupsOpen={signupsOpen} signedIn={signedIn} />
        <Channels />
        <HowItWorks />
        <Film />
        <Product />
        <Pricing signupsOpen={signupsOpen} />
        <Faq />
        <FinalCta signupsOpen={signupsOpen} signedIn={signedIn} />
      </main>
      <Footer />
    </div>
  );
}
